import { CSSProperties, RefObject, useLayoutEffect, useState } from "react";

/** Max font size (px) for quote/invoice footer comments. */
export const COMMENTS_FIT_MAX_FONT_SIZE = 10;
export const COMMENTS_FIT_MIN_FONT_SIZE = 4;
/** Paragraph padding-top (px) at max font size; scales down proportionally. */
export const COMMENTS_FIT_PARAGRAPH_PADDING_AT_MAX_PX = 2;
export const COMMENTS_FIT_PARAGRAPH_PADDING_RATIO =
  COMMENTS_FIT_PARAGRAPH_PADDING_AT_MAX_PX / COMMENTS_FIT_MAX_FONT_SIZE;

export function applyCommentsFitStyles(element: HTMLElement, fontSizePx: number): void {
  element.style.fontSize = `${fontSizePx}px`;
  element.style.setProperty("--comments-font-size", `${fontSizePx}px`);
}

export function getCommentsFitInlineStyles(fontSizePx: number): CSSProperties {
  return {
    fontSize: `${fontSizePx}px`,
    "--comments-font-size": `${fontSizePx}px`,
  } as CSSProperties;
}

export type UseAutoFitFontSizeOptions = {
  enabled?: boolean;
  minFontSize?: number;
  maxFontSize?: number;
  step?: number;
  deps?: unknown[];
};

const DEFAULT_MIN = 5;
const DEFAULT_MAX = 11;
const DEFAULT_STEP = 0.5;

/**
 * Reduces font size on `contentRef` until it fits inside `containerRef`,
 * or until `minFontSize` is reached.
 */
export function useAutoFitFontSize(
  containerRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>,
  options: UseAutoFitFontSizeOptions = {}
): number {
  const {
    enabled = true,
    minFontSize = DEFAULT_MIN,
    maxFontSize: maxFontSizeOption = DEFAULT_MAX,
    step = DEFAULT_STEP,
    deps = [],
  } = options;

  const [fontSize, setFontSize] = useState(maxFontSizeOption);

  useLayoutEffect(() => {
    if (!enabled) {
      setFontSize(maxFontSizeOption);
      return;
    }

    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) {
      return;
    }

    const parent = container.closest(".invoice-info");
    const computedMax = parent
      ? Number.parseFloat(getComputedStyle(parent).fontSize)
      : NaN;
    const inheritedMax =
      Number.isFinite(computedMax) && computedMax > 0 ? computedMax : maxFontSizeOption;
    const maxFontSize = Math.max(maxFontSizeOption, inheritedMax);

    let size = maxFontSize;
    applyCommentsFitStyles(content, size);

    while (size > minFontSize && content.scrollHeight > container.clientHeight) {
      size = Math.max(minFontSize, size - step);
      applyCommentsFitStyles(content, size);
    }

    setFontSize(size);
  }, [enabled, containerRef, contentRef, minFontSize, maxFontSizeOption, step, ...deps]);

  return fontSize;
}
